import React, { Fragment, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { getAllUserRides } from "../../Services/UserRideService";
import Spinner from 'react-bootstrap/Spinner';
import { FaPen } from "react-icons/fa6";
import { Modal, Button, ListGroup } from "react-bootstrap";
import { changeRideStatus } from "../../Services/UserService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const UserRideTable = () => {
    const Columns = [
        {
            name: "User",
            selector: (row) => row.user?.full_name,
            sortable: true,
            center: false,
            minWidth: "200px",
            maxWidth: "300px",
        },
        {
            name: "Source",
            selector: (row) => row.ride?.source?.address,
            sortable: true,
            center: false,
            minWidth: "200px",
            maxWidth: "300px",
        },
        {
            name: "Destination",
            selector: (row) => row.ride?.destination?.address,
            sortable: true,
            center: false,
            minWidth: "200px",
            maxWidth: "300px",
        },
        {
            name: "Driver",
            selector: (row) => row.driver?.full_name,
            sortable: true,
            center: false,
            minWidth: "200px",
            maxWidth: "300px",
        },
        {
            name: "Date",
            selector: (row) => row["createdAt"],
            sortable: true,
            center: false,
            minWidth: "100px",
            maxWidth: "150px",
            cell: (row) => <div>{formattedDate(row?.createdAt)}</div>,
        },
        {
            name: "Time",
            selector: (row) => row.ride?.ride_time,
            sortable: true,
            center: false,
            minWidth: "100px",
            maxWidth: "150px",
        },
        {
            name: "Amount",
            selector: (row) => `${row.ride?.amount} $`,
            sortable: true,
            center: false,
            minWidth: "100px",
            maxWidth: "150px",
        },
        {
            name: "Status",
            selector: (row) => row.status,
            sortable: true,
            center: false,
            minWidth: "100px",
            maxWidth: "150px",
            cell: (row) => {
                const getStyles = () => {
                    switch (row.status) {
                        case "Accepted":
                            return { backgroundColor: "green", color: "white" };
                        case "Rejected":
                            return { backgroundColor: "red", color: "white" };
                        case "Pending":
                            return { backgroundColor: "yellow", color: "black", border: "solid 1px" };
                        case "Canceled":
                            return { backgroundColor: "orange", color: "black", border: "solid 1px" };
                        case "Completed":
                            return { backgroundColor: "SlateBlue", color: "white" };
                        case "Started":
                            return { backgroundColor: "SkyBlue", color: "black", border: "solid 1px" };
                        default:
                            return { backgroundColor: "transparent", color: "black" };
                    }
                };
        
                const styles = getStyles();
        
                return (
                    <>
                        <div
                            style={{
                                ...styles,
                                padding: "5px 10px",
                                borderRadius: "5px",
                                textAlign: "center",
                            }}
                        >
                            {row.status}
                        </div>
                        <div
                            style={{
                                ...styles,
                                marginLeft: "5px",
                                padding: "5px 6px",
                                borderRadius: "5px",
                                cursor: "pointer",
                            }}
                            onClick={() => handleStatusChange(row)}
                        >
                            <FaPen />
                        </div>
                    </>
                );
            },
        },                
    ];

    const [userRideList, setUserRideList] = useState([]);
    const [pending, setPending] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [filteredUserList, setFilteredUserList] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);

    const getAllUserRideList = async () => {
        try {
            const res = await getAllUserRides();
            setUserRideList(res.data);
            setFilteredUserList(res.data);
        } catch (error) {
            console.log(error);
        } finally {
            setPending(false)
        }
    };    

    useEffect(() => {
        getAllUserRideList();
    }, []);

    const formattedDate = (createdDate) => {
        const date = new Date(createdDate);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    };

    useEffect(() => {
        if (!selectedDate) {
            setFilteredUserList(userRideList);
        } else {
            const filtered = userRideList.filter(user => {
                const created = new Date(user.createdAt);
                return created.toDateString() === selectedDate.toDateString();
            });
            setFilteredUserList(filtered);
        }
    }, [selectedDate, userRideList]);

    const handleStatusChange = (row) => {
        setSelectedRow(row);
        setShowEditModal(true);
    };

    const handleStatusUpdated = async () => {
        const passData = {
            _id: selectedRow?._id,
            status: selectedRow?.status,
        };

        const response = await changeRideStatus(passData);

        if (response.success) {
            getAllUserRideList();
            setShowEditModal(false);
        }
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-end align-items-center mb-3">
                <label className="me-2"><strong>Filter by Date:</strong></label>
                <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    placeholderText="Select date"
                    className="form-control"
                    maxDate={new Date()}
                    isClearable
                />
            </div>
            <div className="table-responsive support-table">
                { 
                    pending ? (
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : 
                    (
                        <DataTable
                            columns={Columns}
                            data={filteredUserList}
                            striped={true}
                            center={true}
                            pagination
                        />
                    )
                }
            </div>

            {/* Modal for Edit action */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Driver Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label" style={{ marginRight: "10px" }}>
                            <strong>Change Status</strong>
                        </label>
                        <select
                            className="form-control"
                            value={selectedRow?.status || ""}
                            onChange={(e) => setSelectedRow({ ...selectedRow, status: e.target.value })}
                        >
                            <option value="Accepted">Accepted</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Pending">Pending</option>
                            <option value="Canceled">Canceled</option>
                            <option value="Completed">Completed</option>
                            <option value="Started">Started</option>
                        </select>
                    </div>
                </Modal.Body>
                
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleStatusUpdated}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default UserRideTable;
